'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const STEPS = [
{
  number: '01',
  title: 'Profil erstellen',
  description: 'In 2 Minuten ausgefüllt. Kein langes Fragebogen-Marathon — nur das Wesentliche, damit wir dir echte Matches zeigen können.',
  icon: '✏️',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_111edabae-1770403975885.png",
  imageAlt: 'Person füllt Formular auf einem Smartphone aus',
  accent: '#E8315A',
  span: 'col-span-1 md:col-span-2'
},
{
  number: '02',
  title: 'Lokale Matches entdecken',
  description: 'Wir zeigen dir Singles aus deiner PLZ-Region. Echte Menschen, die du tatsächlich treffen kannst.',
  icon: '📍',
  image: "https://images.unsplash.com/photo-1720815844017-bb175124d548",
  imageAlt: 'Stadtansicht mit Verbindungslinien zwischen Menschen',
  accent: '#FF6B35',
  span: 'col-span-1'
},
{
  number: '03',
  title: 'Verbinden & Treffen',
  description: 'Schreib einfach an — kein Abonnement für die ersten Nachrichten. Vom Chat zum Date.',
  icon: '💬',
  image: null,
  imageAlt: null,
  accent: '#E8315A',
  span: 'col-span-1'
}];


export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.12 }
    );
    sectionRef.current?.querySelectorAll('.reveal, .stagger-children').
    forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 md:py-36 px-4 md:px-6"
    style={{ background: '#0E0B12' }}>
      <div className="max-w-7xl mx-auto">

        <div className="reveal mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest px-5 py-2.5 rounded-full"
          style={{ color: '#FF8CA8', background: 'rgba(232,49,90,0.12)', border: '1px solid rgba(232,49,90,0.25)', boxShadow: '0 4px 16px rgba(232,49,90,0.15)' }}>
            So einfach geht’s
          </span>
          <h2 className="section-headline text-white mt-8">
            In 3 Schritten zu<br />
            <span className="text-gradient italic">deinem ersten Date.</span>
          </h2>
        </div>

        {/* Bento grid — varied sizes */}
        <div className="stagger-children grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Step 01 — wide card */}
          <div className="feature-card rounded-3xl overflow-hidden relative md:col-span-2"
          style={{ background: 'var(--gradient-card)', minHeight: '360px' }}>
            <div className="absolute inset-0 opacity-20">
              <AppImage
                src={STEPS[0].image!}
                alt={STEPS[0].imageAlt!}
                fill
                className="object-cover" />
              
              <div className="absolute inset-0"
              style={{ background: 'linear-gradient(135deg, rgba(12,10,14,0.9) 40%, rgba(232,49,90,0.2))' }} />
            </div>
            <div className="relative z-10 p-9 md:p-12 h-full flex flex-col justify-between">
              <div>
                <span className="text-xs font-black uppercase tracking-widest mb-3 block"
                style={{ color: '#E8315A' }}>
                  {STEPS[0].number}
                </span>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
                  {STEPS[0].icon} {STEPS[0].title}
                </h3>
                <p className="text-base leading-relaxed max-w-md" style={{ color: 'rgba(245,240,247,0.65)' }}>
                  {STEPS[0].description}
                </p>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <div className="px-5 py-2.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(232,49,90,0.18)', color: '#FF8CA8', border: '1px solid rgba(232,49,90,0.3)', boxShadow: '0 4px 12px rgba(232,49,90,0.2)' }}>
                  ⏱ Nur 2 Minuten
                </div>
                <div className="px-5 py-2.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(245,240,247,0.55)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  100% Kostenlos
                </div>
              </div>
            </div>
          </div>

          {/* Step 02 — tall card */}
          <div className="feature-card rounded-3xl overflow-hidden relative"
          style={{ background: 'var(--gradient-card)', minHeight: '360px' }}>
            <div className="absolute inset-0 opacity-15">
              <AppImage
                src={STEPS[1].image!}
                alt={STEPS[1].imageAlt!}
                fill
                className="object-cover" />
              
              <div className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(12,10,14,0.85) 0%, rgba(255,107,53,0.15) 100%)' }} />
            </div>
            <div className="relative z-10 p-9 h-full flex flex-col justify-between">
              <div>
                <span className="text-xs font-black uppercase tracking-widest mb-3 block"
                style={{ color: '#FF6B35' }}>
                  {STEPS[1].number}
                </span>
                <h3 className="font-display text-3xl font-bold text-white mb-4">
                  {STEPS[1].icon} {STEPS[1].title}
                </h3>
                <p className="text-base leading-relaxed" style={{ color: 'rgba(245,240,247,0.65)' }}>
                  {STEPS[1].description}
                </p>
              </div>
              <div className="mt-8 flex -space-x-2">
                {[
                'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=60&h=60&q=80',
                'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=60&h=60&q=80',
                'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=60&h=60&q=80'].
                map((src, i) =>
                <div key={i} className="w-10 h-10 rounded-full overflow-hidden border-[2.5px] transition-transform hover:scale-110 hover:z-10"
                style={{ borderColor: '#0E0B12', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                    <AppImage src={src} alt="Profilbild eines Singles in deiner Nähe" width={40} height={40} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="w-10 h-10 rounded-full border-[2.5px] flex items-center justify-center text-xs font-bold"
                style={{ borderColor: '#0E0B12', background: 'rgba(255,107,53,0.25)', color: '#FF8C5A', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  +
                </div>
              </div>
            </div>
          </div>

          {/* Step 03 — accent card */}
          <div className="feature-card rounded-3xl p-9 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(232,49,90,0.16) 0%, rgba(12,10,14,0.9) 100%)', minHeight: '240px' }}>
            <div className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(232,49,90,0.25) 0%, transparent 70%)' }} />
            <div className="relative z-10">
              <span className="text-xs font-black uppercase tracking-widest mb-3 block"
              style={{ color: '#E8315A' }}>
                {STEPS[2].number}
              </span>
              <h3 className="font-display text-3xl font-bold text-white mb-4">
                {STEPS[2].icon} {STEPS[2].title}
              </h3>
              <p className="text-base leading-relaxed" style={{ color: 'rgba(245,240,247,0.65)' }}>
                {STEPS[2].description}
              </p>
            </div>
          </div>

          {/* CTA mini card */}
          <div className="feature-card rounded-3xl p-9 md:col-span-2 flex flex-col md:flex-row items-center justify-between gap-8"
          style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.1) 0%, rgba(12,10,14,0.9) 100%)' }}>
            <div>
              <p className="font-display text-2xl font-bold text-white mb-2">
                Bereit für dein erstes Match?
              </p>
              <p className="text-base" style={{ color: 'rgba(245,240,247,0.55)' }}>
                Gerade sind <span className="font-semibold" style={{ color: '#22C55E' }}>1.247 Singles</span> aus deiner Region online.
              </p>
            </div>
            <a href="#registration-form"
            className="btn-primary px-9 py-4 rounded-full text-sm font-bold whitespace-nowrap flex-shrink-0">
              Jetzt anmelden →
            </a>
          </div>

        </div>
      </div>
    </section>);

}